<template>
  <CardWithTitle
    :item-id="itemId"
    :items="items"
    :map-keys="mapKeys"
    :request-status="requestStatus"
    :options-data="cardOptions"
    :has-data-to-export="hasDataToExport"
    class="make-a-copy"
    @call-option-menu="callOptionMenu"
    @reload="getData"
  >
    <v-row
      no-gutters
      class="filter-blip-desk-modal"
    >
      <span class="filter-blip-desk-modal__description">
        Os filtros de <span class="filter-blip-desk-modal__contrast">
          Canais, Atendentes, Filas e Tags
        </span> não se aplicam à tabela abaixo.
      </span>
    </v-row>
    <v-data-table
      v-if="hasItems"
      :headers="headers"
      :items="items"
      :items-per-page="itemsPerPage"
      :loading="inputLoading"
      class="table-truncate"
    >
      <template v-slot:[`item.identity`]="{ item }">
        <span>{{ item.identity }}</span>
      </template>
      <template v-slot:[`item.online`]="{ item }">
        <span>{{ item.online }}</span>
      </template>
      <template v-slot:[`item.paused`]="{ item }">
        <span>{{ item.paused }}</span>
      </template>
      <template v-slot:[`item.invisible`]="{ item }">
        <span>{{ item.invisible }}</span>
      </template>
      <template v-slot:[`item.total`]="{ item }">
        <span>{{ item.total }}</span>
      </template>
    </v-data-table>
    <template #top-right-slot>
      <Badge
        v-if="hasFilterApplied && isEditor"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
    </template>
  </CardWithTitle>
</template>

<script>
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import blipModel from '@/mixins/blip-model';
import Badge from '@/components/Badge';
import { mapActions } from 'vuex';
import { timeToSeconds, secondsToTime, remapFieldKeys } from '@/utils/formatter';
import exportModel from '@/mixins/export-model';
import cardsModel from '@/mixins/cards-model';
import { saveAs } from 'file-saver';
import XLSX from 'xlsx';

export default {
  name: 'BlipDeskProductivityTable',
  components: {
    CardWithTitle,
    Badge,
  },
  mixins: [
    exportModel,
    cardsModel,
    blipModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    hasDataToExport: {
      type: Boolean,
      default: true,
    },
    servicePath: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      headers: [
        { text: this.$t('components.BlipDeskComponents.attendant'), value: 'identity' },
        { text: this.$t('components.BlipDeskComponents.status'), value: 'online' },
        { text: this.$t('components.BlipDeskComponents.paused'), value: 'paused' },
        { text: this.$t('components.BlipDeskComponents.invisible'), value: 'invisible' },
        { text: this.$t('components.BlipDeskComponents.total'), value: 'total' },
      ],
      rawSeries: [],
      dialog: false,
      itemsPerPage: 10,
      requestStatus: 'loading',
      mapKeys: {
        identity: this.$t('components.BlipDeskComponents.attendant'),
        online: this.$t('components.BlipDeskComponents.status'),
        paused: this.$t('components.BlipDeskComponents.paused'),
        invisible: this.$t('components.BlipDeskComponents.invisible'),
        total: this.$t('components.BlipDeskComponents.total'),
      },
    };
  },
  computed: {
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    hasItems() {
      return Boolean(this.items.length);
    },
    cardOptions() {
      if (this.hasDataToExport) {
        return this.baseCardOptions.concat(this.exportWithXLSXOptionsData(this.items)).sort((actualOption, prevOption) => this.sortMenuOptions(actualOption, prevOption));
      }
      return this.baseCardOptions;
    },
    items() {
      return this.rawSeries.flatMap((obj) => {
        const resourceItems = obj.resource.items.map((item) => {
          return {
            identity: this.formatIdentity(item.identity),
            online: secondsToTime(timeToSeconds(item.online)),
            paused: secondsToTime(timeToSeconds(item.paused)),
            invisible: secondsToTime(timeToSeconds(item.invisible)),
            total: secondsToTime(timeToSeconds(item.total)),
          };
        });
        return resourceItems;
      });
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getBlipService',
      'hasNoBlipBotAndContractFiltered',
    ]),
    exportWithXLSXOptionsData(items) {
      const exportExtensionOptions = this.exportExtensions.map((extension, index) => (
        {
          order: index + 1,
          name: extension.type,
          function: () => (this.exportComponent(extension, true)),
        }
      ));

      exportExtensionOptions.push({
        order: exportExtensionOptions.length + 1,
        name: 'XLSX',
        function: () => (this.exportComponentXLSX(items)),
      });

      return [
        {
          order: 5,
          name: this.$t('mixins.exportModel.label.exportLabel'),
          iconname: 'download',
          function: () => ([]),
          child: exportExtensionOptions,
          trackName: 'ST CardMenu Export Click',
          trackProperties: {
            title: this.card?.props?.title,
            widget: this.card?.component,
          },
          isExpandedModeOption: true,
        },
      ];
    },
    exportComponentXLSX(items) {
      const translatedData = remapFieldKeys(this.mapKeys, items);
      const ws = XLSX.utils.json_to_sheet(translatedData);
      const wb = { Sheets: { 'data': ws }, SheetNames: ['data'] };
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      });
      saveAs(blob, `${this.title}.xlsx`);
    },
    async getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      if (await this.hasNoBlipBotAndContractFiltered(this.itemId)) {
        this.requestStatus = 'emptyBot';
        this.items = [];

        return;
      }
      this.getBlipService({
        servicePath: this.servicePath,
        itemId: this.itemId,
      })
        .then((responses) => {
          if (!options?.silentRefresh) {
            this.requestStatus = 'loading';
          }

          this.rawSeries = responses;

          this.requestStatus = 'success';
        })
        .catch(error => {
          console.error(error);
          this.items = [];
          this.requestStatus = error.message;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.filter-blip-desk-modal {
  position: relative;
  margin: 0 0 5px 0 !important;
  line-height: 20px;
  color: var(--ds-color-text-1);
  font-size: var(--ds-font-size-sm);

  &__description {
    font-weight: var(--ds-font-weight-regular);
  }

  &__contrast {
    font-weight: var(--ds-font-weight-bold);
  }
}

.v-select__selection.v-select__selection--comma {
  line-height: 120%;
}

.internal-style {
  width: 100%;
  display: flex;
}

.buttons {
  display: grid;
}

.clean-sla-buttons {
  box-shadow: none;
  border-radius: unset;
}

.close-button {
  display: flex;
  flex: end;
  justify-content: flex-end;
}

::v-deep {
  .reload-and-loader-wrapper {
    height: 100%;
    overflow-y: auto;

    &::-webkit-scrollbar {
      display: block !important;
    }
  }

  .table-truncate {
    td {
      max-width: 10vw;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }

    h3 {
      font-weight: var(--ds-font-weight-bold);
    }
  }
}
</style>
